import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { ParticleField } from "../effects";
import { MagneticButton, Reveal } from "../helpers";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const onMouseMove = (e) => {
    const { innerWidth: w, innerHeight: h } = window;
    const px = (e.clientX / w - 0.5) * 2;
    const py = (e.clientY / h - 0.5) * 2;
    ref.current?.style.setProperty("--px", px);
    ref.current?.style.setProperty("--py", py);
  };

  const words = "BUILDING SMART SOLUTIONS FOR A".split(" ");

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16"
    >
      {/* moving grid */}
      <div
        className="absolute inset-0 opacity-[0.15]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.08) 1px,transparent 1px)",
          backgroundSize: "60px 60px",
          transform: "translate(calc(var(--px,0)*-8px), calc(var(--py,0)*-8px))",
        }}
      />
      {/* aurora glow */}
      <div className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full blur-[120px] opacity-40"
        style={{ background: "radial-gradient(circle,#FF5F00,transparent 70%)" }} />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] opacity-25"
        style={{ background: "radial-gradient(circle,#4AF0FF,transparent 70%)" }} />

      <ParticleField />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center w-full">
        <motion.div style={{ opacity }}>
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-body text-brand-sec mb-8">
              <Sparkles className="h-3.5 w-3.5 text-brand-orange" strokeWidth={1.5} />
              Creative Digital & AI Solutions
            </span>
          </Reveal>

          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {words.map((w, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ duration: 0.7, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="inline-block mr-3"
              >
                {w}
              </motion.span>
            ))}
            <br className="hidden sm:block" />
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="text-gradient-orange-anim inline-block"
            >
              BETTER TOMORROW
            </motion.span>
          </h1>

          <Reveal delay={0.7}>
            <p className="mt-8 font-body text-base sm:text-lg text-brand-sec max-w-xl leading-relaxed">
              Paramarsh Tech is a creative digital agency delivering modern, scalable, and
              intelligent solutions that help businesses grow, automate, and succeed in the
              digital era.
            </p>
          </Reveal>

          <Reveal delay={0.85}>
            <div className="mt-10 flex flex-wrap gap-4">
              <MagneticButton
                data-testid="hero-build-btn"
                onClick={() => scrollTo("contact")}
                className="group inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-medium text-black shadow-[0_0_40px_rgba(255,138,0,0.35)]"
                style={{ background: "linear-gradient(to right,#FF5F00,#FF9D00,#FFC400)" }}
              >
                Let&apos;s Build Together
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </MagneticButton>
              <MagneticButton
                data-testid="hero-services-btn"
                onClick={() => scrollTo("services")}
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-medium text-white glass hover:border-brand-orange/40 border border-white/10 transition-colors"
              >
                Explore Services
              </MagneticButton>
            </div>
          </Reveal>
        </motion.div>

        {/* Logo visual */}
        <motion.div style={{ y: yImg }} className="relative flex items-center justify-center">
          <div className="relative w-[320px] h-[320px] sm:w-[420px] sm:h-[420px]">
            {/* rings */}
            <div className="absolute inset-0 rounded-full border border-brand-orange/20 animate-spinslow" />
            <div className="absolute inset-6 rounded-full border border-white/10 animate-spinslow" style={{ animationDirection: "reverse", animationDuration: "30s" }} />
            <div className="absolute inset-16 rounded-full border border-brand-accent/15" />
            {/* glow */}
            <div className="absolute inset-10 rounded-full blur-[60px] opacity-50"
              style={{ background: "radial-gradient(circle,#FF8A00,transparent 70%)" }} />
            <motion.img
              src="/paramarsh-logo.webp"
              alt="Paramarsh Tech logo"
              className="absolute inset-0 m-auto w-56 sm:w-72 h-auto object-contain animate-float rounded-3xl border border-brand-orange/25"
              style={{ filter: "drop-shadow(0 0 40px rgba(255,138,0,0.45))" }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
